/** 出発ウォッチ (Web Push) のクライアント側ヘルパー */
import { createWatch, fetchPushKey } from './api';

export type WatchSupport = 'ok' | 'unsupported' | 'denied';

/** この環境で Web Push が使えるか (許可状態は問わない) */
export function pushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** 通知許可 → SW 準備 → push 購読 (既存があれば再利用) */
export async function ensurePushSubscription(): Promise<PushSubscriptionJSON> {
  if (!pushSupported()) {
    throw new WatchError('unsupported', 'このブラウザは通知に対応していません');
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    throw new WatchError('denied', '通知が許可されていません');
  }
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { publicKey } = await fetchPushKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey) as unknown as BufferSource,
    });
  }
  return sub.toJSON();
}

export class WatchError extends Error {
  constructor(public kind: WatchSupport | 'failed', message: string) {
    super(message);
    this.name = 'WatchError';
  }
}

function splitUid(uid: string): [string, string] {
  const i = uid.lastIndexOf('|');
  return [uid.slice(0, i), uid.slice(i + 1)];
}

/**
 * 「この便が出発 leadMinutes 分前になったら知らせて」を登録する。
 * leadMinutes には徒歩時間 + 準備バッファを渡す想定。
 */
export async function watchDeparture(opts: {
  tripUid: string;
  stopUid: string;
  serviceDate: string;
  leadMinutes: number;
}): Promise<{ watchId: string }> {
  const subscription = await ensurePushSubscription();
  const [feedId, tripId] = splitUid(opts.tripUid);
  const [, stopId] = splitUid(opts.stopUid);
  try {
    return await createWatch({
      subscription,
      feedId,
      tripId,
      stopId,
      serviceDate: opts.serviceDate,
      leadMinutes: Math.max(1, Math.round(opts.leadMinutes)),
    });
  } catch (err) {
    if (err instanceof WatchError) throw err;
    throw new WatchError('failed', 'ウォッチ登録に失敗しました');
  }
}
